import React, { useState, useRef } from 'react';
import { X, Image as ImageIcon } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useUser } from '../contexts/UserContext';

interface CreateChannelDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

function CreateChannelDialog({ isOpen, onClose, onSuccess }: CreateChannelDialogProps) {
  const { user } = useUser();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [coverImage, setCoverImage] = useState<File | null>(null);
  const [coverPreview, setCoverPreview] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setError('Please upload an image file');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      setError('File size should be less than 5MB');
      return;
    }

    setCoverImage(file);
    setCoverPreview(URL.createObjectURL(file));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user || submitting) return;

    setSubmitting(true);
    setError(null);

    try {
      // Validate channel name
      if (!name.match(/^[a-zA-Z0-9_-]{3,30}$/)) {
        throw new Error('Channel name must be 3-30 characters and can only contain letters, numbers, underscores, and hyphens');
      }

      let coverImageUrl = null;

      // Upload cover image if selected
      if (coverImage) {
        const fileExt = coverImage.name.split('.').pop();
        const fileName = `${Math.random()}.${fileExt}`;
        const filePath = `channel-covers/${fileName}`;

        const { error: uploadError } = await supabase.storage
          .from('user-content')
          .upload(filePath, coverImage, {
            upsert: true,
            cacheControl: '3600',
            contentType: coverImage.type
          });

        if (uploadError) throw uploadError;

        const { data: { publicUrl } } = supabase.storage
          .from('user-content')
          .getPublicUrl(filePath);

        coverImageUrl = publicUrl;
      }

      // Create channel
      const { error: channelError } = await supabase
        .from('channels')
        .insert({
          name: name.toLowerCase(),
          description: description.trim(),
          creator_id: user.id,
          cover_image: coverImageUrl
        });

      if (channelError) throw channelError;

      // Add creator as owner
      const { data: channel } = await supabase
        .from('channels')
        .select('id')
        .eq('name', name.toLowerCase())
        .single();

      if (channel) {
        const { error: memberError } = await supabase
          .from('channel_members')
          .insert({
            channel_id: channel.id,
            user_id: user.id,
            role: 'owner'
          });

        if (memberError) throw memberError;
      }

      onClose();
      if (onSuccess) onSuccess();
    } catch (error) {
      console.error('Error creating channel:', error);
      setError(error instanceof Error ? error.message : 'Failed to create channel');
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-[60] flex items-center justify-center p-4">
      <div className="bg-white rounded-lg w-full max-w-md max-h-[80vh] flex flex-col">
        <div className="p-4 border-b flex justify-between items-center flex-shrink-0">
          <h3 className="text-lg font-semibold">Create Channel</h3>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <X className="h-5 w-5 text-gray-500" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-4 space-y-4 overflow-y-auto">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Channel Name
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g., seventies-rock"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
              required
            />
            <p className="mt-1 text-xs text-gray-500">
              Only letters, numbers, underscores, and hyphens allowed
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Description
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="What's this channel about?"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 resize-none"
              rows={3}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Cover Image
            </label>
            <div 
              onClick={() => fileInputRef.current?.click()}
              className="cursor-pointer border-2 border-dashed border-gray-300 rounded-lg p-4 hover:border-indigo-500 transition-colors"
            >
              {coverPreview ? (
                <div className="relative">
                  <img
                    src={coverPreview}
                    alt="Cover preview"
                    className="w-full h-32 object-cover rounded-lg"
                  />
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setCoverImage(null);
                      setCoverPreview(null);
                    }}
                    className="absolute top-2 right-2 p-1 bg-black bg-opacity-50 rounded-full text-white hover:bg-opacity-75 transition-opacity"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center h-32 text-gray-500">
                  <ImageIcon className="h-12 w-12 mb-2" />
                  <p className="text-sm">Click to upload cover image</p>
                  <p className="text-xs mt-1">Max size: 5MB</p>
                </div>
              )}
            </div>
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileSelect}
              accept="image/*"
              className="hidden"
            />
          </div>

          {error && (
            <p className="text-sm text-red-600">{error}</p>
          )}

          <button
            type="submit"
            disabled={submitting}
            className={`w-full py-2 px-4 rounded-lg text-white ${
              submitting
                ? 'bg-indigo-400 cursor-not-allowed'
                : 'bg-indigo-600 hover:bg-indigo-700'
            } transition-colors`}
          >
            {submitting ? 'Creating...' : 'Create Channel'}
          </button>
        </form>
      </div>
    </div>
  );
}

export default CreateChannelDialog;